import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import { clearColorPreferenceCache } from '../hooks/useColorPreferences';
import { clearOfflineData } from '../offline/db';
import { noteNetworkError } from '../offline/offlineNotice';

const AuthContext = createContext(null);

// PostHog was capturing one `api_request` event for every single API call,
// which is expensive under load. Throttle successful-request captures to at
// most one per interval (errors are still always captured below), so a burst
// of calls no longer emits one event each.
const API_CAPTURE_MIN_INTERVAL_MS = 10000;
let lastApiRequestCaptureAt = 0;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const logoutTriggeredRef = useRef(false);

  useEffect(() => {
    // Check if setup is required (no users exist) and if user is logged in
    const checkInitialState = async () => {
      try {
        // First check if setup is required
        const setupResponse = await fetch(`${API_URL}/api/auth/setup/status`);
        if (setupResponse.ok) {
          const setupData = await setupResponse.json();
          if (setupData.setup_required) {
            setSetupRequired(true);
            setLoading(false);
            return; // Don't check for user session if setup is required
          }
        }
      } catch (err) {
        // If we can't reach the server, continue with normal flow
        console.error('Could not check setup status:', err);
      }

      // Check if user is already logged in
      const token = localStorage.getItem('auth_token');
      if (token) {
        // Optimistically rehydrate the cached user so an offline start keeps the session.
        const cachedUser = localStorage.getItem('auth_user');
        if (cachedUser) {
          try { setCurrentUser(JSON.parse(cachedUser)); }
          catch (_) { localStorage.removeItem('auth_user'); }
        }
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setCurrentUser(data.user);
            localStorage.setItem('auth_user', JSON.stringify(data.user));
          } else if (response.status === 401 || response.status === 403) {
            // Real auth failure -> clear the session.
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            setCurrentUser(null);
          }
          // Other non-ok (5xx etc.): keep the (optimistically rehydrated) session; do not clear.
        } catch (err) {
          // Network error (offline): keep the session; the cached user is already set.
          // Do NOT remove the token here — that is the offline-logout bug.
        }
      }
      setLoading(false);
    };

    checkInitialState();
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        // Parse the JSON error envelope so the user sees a clean human message
        // ({message: '...'}) instead of a raw JSON blob from response.text().
        const body = await response.json().catch(() => null);
        const message = body?.message || 'Login failed';

        // Track login failure
        if (window.posthog) {
          window.posthog.capture('login_failed', {
            username,
            reason: message
          });
        }

        throw new Error(message);
      }

      // Capture the previous device's last-logged-in user id BEFORE we
      // overwrite it, so we can tell whether this login is the same person
      // re-authenticating (e.g. an expired JWT while offline writes are
      // still queued) or a different person on a shared device.
      //
      // We deliberately key this off 'last_user_id' rather than 'auth_user':
      // both the init-effect 401/403 handler and authFetch's 401 handler
      // remove 'auth_user' the moment a token is detected expired/revoked —
      // which is exactly the "my JWT expired, forced to re-login" flow this
      // check exists for. By the time login() ran, 'auth_user' would already
      // be null, so sameUser was always false and the outbox got purged on
      // every expired-session re-login. 'last_user_id' is never cleared by
      // any 401/403 handler or by logout(), so it survives token expiry and
      // reliably identifies who last used this device.
      const prevUserId = localStorage.getItem('last_user_id');

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      const newUser = data.user;
      setCurrentUser(newUser);
      localStorage.setItem('auth_user', JSON.stringify(newUser));
      setError(null);

      // Purge any previous user's offline Dexie store + SW api cache — but
      // ONLY when a different user just logged in. On a shared browser/PWA
      // where the previous user never logged out, this stops user B's login
      // from briefly showing user A's cached data. If the SAME user is just
      // re-authenticating (e.g. their token expired while they had unsynced
      // offline ASB/work-hour writes queued in the outbox), purging would
      // silently discard that pending work — so we keep it.
      // clearOfflineData() does not touch localStorage, so the auth_token/
      // auth_user just persisted above survive either way. Best-effort: a
      // Dexie/cache failure here must never block a successful login.
      const currentId = newUser?.id != null ? String(newUser.id) : (newUser?.username ?? null);
      const sameUser = prevUserId != null && currentId != null && prevUserId === currentId;
      if (!sameUser) {
        try {
          await clearOfflineData();
        } catch (err) {
          console.error('Failed to clear offline data on login:', err);
        }
      }
      if (currentId != null) {
        localStorage.setItem('last_user_id', currentId);
      }

      // Track successful login
      if (window.posthog) {
        window.posthog.capture('user_logged_in', {
          user_role: data.user.role,
          has_teams: (data.user.teams?.length || 0) > 0,
        });
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // Track logout before clearing state
      if (window.posthog) {
        window.posthog.capture('user_logged_out');
      }

      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Purge the offline Dexie store so a shared browser/PWA never carries
      // the previous user's cached appointments or pending outbox jobs into
      // the next login. Wrapped in try/catch: a Dexie failure must never
      // block logout from clearing the token/user.
      try {
        await clearOfflineData();
      } catch (err) {
        console.error('Failed to clear offline data on logout:', err);
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      clearColorPreferenceCache();
      setCurrentUser(null);
      setLoading(false);
    }
  }, []);


  // frontend/src/context/AuthContext.js
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const startTime = performance.now();

    // Set up headers with Authorization but don't use credentials: 'include'
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    // Only set Content-Type if explicitly provided or if body is not FormData
    // For FormData, browser must set Content-Type automatically with boundary
    // Don't set Content-Type for GET requests (file downloads need original Content-Type from server)
    const method = options.method?.toUpperCase() || 'GET';
    if (options.headers?.['Content-Type']) {
      headers['Content-Type'] = options.headers['Content-Type'];
    } else if (!(options.body instanceof FormData) && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    try {
      // Isolate the fetch() call itself: only a genuine network-level
      // failure here (thrown by fetch, not a 4xx/5xx response) means the
      // network is actually down. That's the only case that should mark a
      // network error / trigger the offline notice -- a real HTTP response
      // (including the 401 handling below) must NOT be treated as offline.
      let response;
      try {
        response = await fetch(fullUrl, {
          ...options,
          headers,
          mode: 'cors',
          // Remove credentials: 'include' since we're using Authorization header
        });
      } catch (netErr) {
        noteNetworkError();
        throw netErr;
      }

      // Track API performance. Always capture failed requests; sample
      // successful ones at most once per interval to avoid one event per call.
      const duration = performance.now() - startTime;
      if (window.posthog) {
        const now = Date.now();
        if (!response.ok || now - lastApiRequestCaptureAt >= API_CAPTURE_MIN_INTERVAL_MS) {
          lastApiRequestCaptureAt = now;
          window.posthog.capture('api_request', {
            endpoint: url.replace(API_URL, ''),
            method: method,
            status: response.status,
            duration_ms: Math.round(duration),
            success: response.ok,
          });
        }
      }

      if (response.status === 401) {
        // Only trigger logout if we have an active session (avoid clearing fresh login tokens)
        const currentToken = localStorage.getItem('auth_token');
        if (!logoutTriggeredRef.current && currentToken && currentToken === token) {
          logoutTriggeredRef.current = true;
          console.error('Session expired, logging out');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          setCurrentUser(null);
          // Reset after short delay so future real 401s still work
          setTimeout(() => { logoutTriggeredRef.current = false; }, 2000);
        }
        throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
      }

      return response;
    } catch (error) {
      // Track API errors
      const duration = performance.now() - startTime;
      if (window.posthog) {
        window.posthog.capture('api_error', {
          endpoint: url.replace(API_URL, ''),
          method: method,
          error: error.message,
          duration_ms: Math.round(duration),
        });
      }

      // Better error logging for CORS issues
      if (error.message.includes('CORS')) {
        console.error('CORS Error:', error.message);
        console.error('Request URL:', fullUrl);
        console.error('Request Method:', options.method || 'GET');
      } else {
        console.error('API request error:', error);
      }
      throw error;
    }
  }, []);

  // apiFetch: opt-in wrapper on top of authFetch that centralizes the response
  // handling that call sites currently duplicate. It checks response.ok, throws
  // an Error carrying the parsed {message} (instead of leaking a raw body), and
  // unwraps the {message,data} envelope so callers get `data` directly.
  //
  // This is ADDITIVE and does not change authFetch: existing callers that use
  // authFetch and do their own `data.data || data` unwrap keep working. New/
  // migrated callers can use apiFetch for the cleaner contract.
  //
  // 401 is handled centrally inside authFetch (session expiry → logout). 403 is
  // surfaced here as a distinct permission error (err.status === 403,
  // err.isPermissionError) without logging the user out.
  const apiFetch = useCallback(async (url, options = {}) => {
    const response = await authFetch(url, options);

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body?.message ||
        (response.status === 403
          ? 'Keine Berechtigung für diese Aktion.'
          : `Anfrage fehlgeschlagen (${response.status})`);
      const error = new Error(message);
      error.status = response.status;
      error.body = body;
      if (response.status === 403) {
        error.isPermissionError = true;
      }
      throw error;
    }

    // Empty body (e.g. 204 No Content) → nothing to parse.
    const text = await response.text();
    if (!text) return null;

    let json;
    try {
      json = JSON.parse(text);
    } catch {
      // Non-JSON payload (e.g. a file/text download) → return as-is.
      return text;
    }

    // Unwrap the {message,data} envelope when present.
    if (json && typeof json === 'object' && !Array.isArray(json) && 'data' in json) {
      return json.data;
    }
    return json;
  }, [authFetch]);

  // Get current token from localStorage
  const token = localStorage.getItem('auth_token');

  const value = useMemo(() => ({
    currentUser,
    loading,
    error,
    setupRequired,
    token,
    login,
    logout,
    authFetch,
    apiFetch
  }), [currentUser, loading, error, setupRequired, token, login, logout, authFetch, apiFetch]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);